function defaultLaborMappingCompanyNumber(assignedCompanyNumbers) {
  const assigned = (assignedCompanyNumbers ?? []).filter(Boolean);
  return assigned.length === 1 ? String(assigned[0] ?? "").trim() : "";
}

function normalizeOpcodeEntries(value) {
  if (!Array.isArray(value)) {
    return [];
  }
  const list = [];
  for (const raw of value) {
    if (typeof raw === "string") {
      const opCode = String(raw ?? "").trim();
      if (opCode) {
        list.push({ opCode, jobNo: "", companyNumber: "" });
      }
      continue;
    }
    if (raw && typeof raw === "object") {
      list.push({
        opCode: String(raw.opCode ?? raw.op_code ?? "").trim(),
        jobNo: String(raw.jobNo ?? raw.job_no ?? "").trim(),
        companyNumber: String(raw.companyNumber ?? raw.company_number ?? "").trim(),
      });
    }
  }
  return list;
}

export function createEmptyLaborMappingRow(assignedCompanyNumbers = []) {
  const nums = (assignedCompanyNumbers ?? []).filter(Boolean);
  return {
    id: `lm-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    companyNumber: nums.length > 0 ? nums[0] : "",
    jobNo: "",
  };
}

export function laborMappingRowsFromOpCodes(opCodes, assignedCompanyNumbers = []) {
  const entries = normalizeOpcodeEntries(opCodes);
  const seen = new Set();
  const rows = [];
  for (const entry of entries) {
    const jobNo = String(entry.jobNo ?? "").trim();
    if (!jobNo) {
      continue;
    }
    const companyNumber =
      String(entry.companyNumber ?? "").trim() ||
      defaultLaborMappingCompanyNumber(assignedCompanyNumbers);
    const key = `${companyNumber}\0${jobNo}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    rows.push({
      id: `lm-saved-${rows.length}-${jobNo}`,
      companyNumber,
      jobNo,
    });
  }
  return rows.sort((a, b) => {
    const byCompany = a.companyNumber.localeCompare(b.companyNumber, undefined, {
      sensitivity: "base",
    });
    if (byCompany !== 0) {
      return byCompany;
    }
    return a.jobNo.localeCompare(b.jobNo, undefined, { sensitivity: "base" });
  });
}

export function ensureLaborMappingRows(service, assignedCompanyNumbers = []) {
  const rows = service?.laborMappingRows;
  if (Array.isArray(rows) && rows.length > 0) {
    return rows;
  }
  const fromOpCodes = laborMappingRowsFromOpCodes(service?.opCodes, assignedCompanyNumbers);
  if (fromOpCodes.length > 0) {
    return fromOpCodes;
  }
  return [createEmptyLaborMappingRow(assignedCompanyNumbers)];
}

export function buildOpCodesFromLaborMappingRows(
  laborMappingRows,
  opCodeStrings,
  assignedCompanyNumbers = []
) {
  const rows = (laborMappingRows ?? [])
    .map((row) => ({
      companyNumber:
        String(row.companyNumber ?? "").trim() ||
        defaultLaborMappingCompanyNumber(assignedCompanyNumbers),
      jobNo: String(row.jobNo ?? "").trim(),
    }))
    .filter((row) => row.jobNo);
  const codes = (opCodeStrings ?? []).map((code) => String(code ?? "").trim()).filter(Boolean);
  const result = [];
  if (codes.length === 0) {
    for (const row of rows) {
      result.push({ opCode: "", jobNo: row.jobNo, companyNumber: row.companyNumber });
    }
    return result;
  }
  for (const opCode of codes) {
    for (const row of rows) {
      result.push({
        opCode,
        jobNo: row.jobNo,
        companyNumber: row.companyNumber,
      });
    }
  }
  return result;
}

const LABOR_MAPPING_DUPLICATE_MESSAGE =
  "This company number and jacket combination is already used.";

function resolvedLaborMappingRowKey(row, assignedCompanyNumbers = []) {
  const companyNumber =
    String(row?.companyNumber ?? "").trim() ||
    defaultLaborMappingCompanyNumber(assignedCompanyNumbers);
  const jobNo = String(row?.jobNo ?? "").trim();
  if (!jobNo) {
    return "";
  }
  return `${companyNumber}\0${jobNo}`;
}

function applyLaborMappingDuplicateErrors(rowErrors, rows, assignedCompanyNumbers = []) {
  const list = Array.isArray(rows) ? rows : [];
  const errors = (rowErrors ?? []).map((err) => (err ? { ...err } : null));
  const keyToIndexes = new Map();

  list.forEach((row, index) => {
    const key = resolvedLaborMappingRowKey(row, assignedCompanyNumbers);
    if (!key) {
      return;
    }
    const indexes = keyToIndexes.get(key) ?? [];
    indexes.push(index);
    keyToIndexes.set(key, indexes);
  });

  const assigned = (assignedCompanyNumbers ?? []).filter(Boolean);
  for (const indexes of keyToIndexes.values()) {
    if (indexes.length <= 1) {
      continue;
    }
    for (const index of indexes) {
      const existing = errors[index] ?? {};
      errors[index] = {
        ...existing,
        jobNo: LABOR_MAPPING_DUPLICATE_MESSAGE,
        ...(assigned.length > 1 ? { companyNumber: LABOR_MAPPING_DUPLICATE_MESSAGE } : {}),
      };
    }
  }

  return errors;
}

export function validateSingleLaborMappingRowErrors(row, assignedCompanyNumbers = []) {
  const err = {};
  const assigned = (assignedCompanyNumbers ?? []).filter(Boolean);
  const companyNumber =
    String(row?.companyNumber ?? "").trim() || defaultLaborMappingCompanyNumber(assigned);
  if (assigned.length > 1 && !companyNumber) {
    err.companyNumber = "Company number is required.";
  }
  if (!String(row?.jobNo ?? "").trim()) {
    err.jobNo = "Jacket is required.";
  }
  return Object.keys(err).length > 0 ? err : null;
}

export function isLaborMappingRowComplete(row, assignedCompanyNumbers = []) {
  return validateSingleLaborMappingRowErrors(row, assignedCompanyNumbers) == null;
}

export function validateLaborMappingRows(rows, assignedCompanyNumbers = []) {
  const list = Array.isArray(rows) ? rows : [];
  if (list.length === 0) {
    return [{ _row: "Add at least one labor mapping." }];
  }
  const rowErrors = applyLaborMappingDuplicateErrors(
    list.map((row) => validateSingleLaborMappingRowErrors(row, assignedCompanyNumbers)),
    list,
    assignedCompanyNumbers
  );
  return rowErrors.some(Boolean) ? rowErrors : null;
}

export function serviceHasLaborMappingRows(service) {
  const rows = service?.laborMappingRows ?? [];
  if (rows.some((row) => String(row?.jobNo ?? "").trim())) {
    return true;
  }
  return laborMappingRowsFromOpCodes(service?.opCodes).length > 0;
}

export { defaultLaborMappingCompanyNumber };
